import * as React from 'react';
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  InteractionStatus
} from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';

import { setAuthTokenProvider } from '@cdf/api-client-react';
import {
  buildLoginRequest,
  buildTokenRequest,
  getApiScopes,
  getPostLogoutRedirectUri,
  getRedirectUri
} from '@/lib/auth-config';
import { isInIframe, openTopLevelAuth } from '@/lib/auth-navigation';
import { useAuthMode } from '@/components/auth/auth-mode';

export interface AuthContextValue {
  enabled: boolean;
  ready: boolean;
  isAuthenticated: boolean;
  account: { name: string; email: string | null } | null;
  login: () => void;
  logout: () => void;
  getToken: () => Promise<string | null>;
}

const DISABLED_AUTH: AuthContextValue = {
  enabled: false,
  ready: true,
  isAuthenticated: false,
  account: null,
  login: () => undefined,
  logout: () => undefined,
  getToken: async () => null
};

const AuthContext = React.createContext<AuthContextValue>(DISABLED_AUTH);

function useMsalAuthValue(): AuthContextValue {
  const { instance, inProgress } = useMsal();
  // Reactive MSAL hooks only during render — never getActiveAccount()/getAllAccounts().
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = useAccount();
  const ready = inProgress === InteractionStatus.None;
  const iframe = React.useMemo(() => isInIframe(), []);

  const login = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (iframe) {
      openTopLevelAuth('login');
      return;
    }

    void instance.loginRedirect(buildLoginRequest(window.location.href)).catch((error: unknown) => {
      console.error('MSAL loginRedirect failed', error);
    });
  }, [instance, inProgress, iframe]);

  const logout = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (iframe) {
      openTopLevelAuth('logout');
      return;
    }

    // Resolve account at call time — avoids stale event-listener closures.
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;
    void instance
      .logoutRedirect({
        account,
        postLogoutRedirectUri: getPostLogoutRedirectUri()
      })
      .catch((error: unknown) => {
        console.error('MSAL logoutRedirect failed', error);
      });
  }, [instance, inProgress, iframe]);

  const getToken = React.useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;

    const apiScopes = getApiScopes();
    const request = {
      ...buildTokenRequest(account),
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
    };

    try {
      const result = await instance.acquireTokenSilent(request);
      // Only a delegated access token (ENTRA_API_SCOPE) is sent to the API.
      // The API rejects ID tokens, so without an API scope configured no
      // bearer is sent and protected calls return 401 until one is provisioned.
      return apiScopes ? result.accessToken || null : null;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        if (iframe) {
          openTopLevelAuth('acquireToken');
          return null;
        }
        await instance.acquireTokenRedirect({
          ...request,
          redirectUri: getRedirectUri(),
          redirectStartPage: window.location.href
        });
      }
      return null;
    }
  }, [instance, iframe]);

  React.useEffect(() => {
    setAuthTokenProvider(getToken);
    return () => setAuthTokenProvider(null);
  }, [getToken]);

  const account = activeAccount
    ? {
        name: activeAccount.name ?? activeAccount.username,
        email: activeAccount.username || null
      }
    : null;

  return {
    enabled: true,
    ready,
    isAuthenticated,
    account,
    login,
    logout,
    getToken
  };
}

/** Provides MSAL-backed auth when the root bootstrap mounted MsalProvider. */
function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMsalAuthValue();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Auth context owner.
 * When authentication is disabled, children receive an honest dormant provider.
 * When enabled, must be rendered under MsalProvider (see main.tsx).
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { enabled } = useAuthMode();
  if (!enabled) {
    return <AuthContext.Provider value={DISABLED_AUTH}>{children}</AuthContext.Provider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}

/**
 * User-triggered auth operations only.
 * No URL/query effects — AuthLifecycle owns those side effects.
 */
export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}
